import React, { ReactElement, ReactNode, useEffect } from 'react';
import { useFormContext } from 'react-hook-form';
import { BiCarousel } from '@react-icons/all-files/bi/BiCarousel';
import { RiNumber1 } from '@react-icons/all-files/ri/RiNumber1';
import { RiNumber2 } from '@react-icons/all-files/ri/RiNumber2';
import { RiNumber3 } from '@react-icons/all-files/ri/RiNumber3';
import { ButtonGroupField, Option, SelectField, TextInputField, ToggleField } from '@bucketplace/commerce-admin-ui';

import {
  FormContent,
  InspectorFormProvider,
  LogDataFormGroup,
} from '../../../components';
import { ColType } from '../../../components/Exhibitions/types';
import { useStoreItemsContext } from '../../../contexts';
import { DocumentInspectorProps } from '../../../types';
import { ProductListProps } from '../ProductList';
import { PRODUCT_LIST_SELECTION_TYPES, ProductListSelectionType, ProductTextBadge, RANKING_FILTER_TYPES, RankingFilterType } from '../types';
import { ProductTextBadgeList } from './ProductTextBadgeList';

const PRODUCT_LIST_SELECTION_TYPE_TEXTS: Record<ProductListSelectionType, string> = {
  MANUAL: '직접 선택',
  RANKING: '랭킹',
  PERSONALIZED: '개인화 추천',
};

const PRODUCT_LIST_SELECTION_TYPE_OPTIONS = PRODUCT_LIST_SELECTION_TYPES.map((type) => ({
  value: type,
  text: PRODUCT_LIST_SELECTION_TYPE_TEXTS[type],
}));

const RANKING_FILTER_TYPE_TEXTS: Record<RankingFilterType, string> = {
  CATEGORY: '카테고리',
  BRAND: '브랜드',
};

const RANKING_FILTER_TYPE_OPTIONS = RANKING_FILTER_TYPES.map((type) => ({
  value: type,
  text: RANKING_FILTER_TYPE_TEXTS[type],
}));

export const PRODUCT_LIST_TYPES: {
  text: ReactElement,
  value: ColType,
  canUseType: ProductListProps['badgeType'][],
}[] = [
  { text: <RiNumber1 />, value: 1, canUseType: ['none', 'banner', 'circle', 'small'] },
  { text: <RiNumber2 />, value: 2, canUseType: ['none', 'banner', 'circle', 'small'] },
  { text: <RiNumber3 />, value: 3, canUseType: ['none', 'small'] },
  { text: <BiCarousel />, value: 'carousel', canUseType: ['none', 'small'] },
];

export const PRODUCT_BADGE_TYPES: {
  text: string,
  value: ProductListProps['badgeType'],
  canUseType: ColType[],
}[] = [
  { text: 'none', value: 'none', canUseType: [1, 2, 3, 'carousel'] },
  { text: 'banner', value: 'banner', canUseType: [1, 2] },
  { text: 'circle', value: 'circle', canUseType: [1, 2] },
  { text: 'small', value: 'small', canUseType: [1, 2, 3, 'carousel'] },
];

export function ProductListInspector({
  value,
  onChange,
}: DocumentInspectorProps<ProductListProps>): ReactElement {
  const isManualSelection = value.props.selectionType === 'MANUAL';

  const handleChangeTextBadgeList = (newProductTextBadgeList: ProductTextBadge[]): void => {
    onChange({
      ...value,
      props: {
        ...value.props,
        products: [...newProductTextBadgeList],
      },
    });
  };

  return (
    <InspectorFormProvider value={value} onChange={onChange}>
      <FormContent
        header="타이틀"
      >
        <TextInputField
          name="title"
          width="100%"
        />
      </FormContent>

      <FormContent
        header="서브타이틀"
      >
        <TextInputField
          name="subtitle"
          width="100%"
        />
      </FormContent>

      <FormContent header="상품 선택 방식">
        <SelectField
          data-testid="productListSelectionType"
          name="selectionType"
          width="100%"
          options={PRODUCT_LIST_SELECTION_TYPE_OPTIONS}
        />
      </FormContent>

      { isManualSelection && (
        <>
          <FormContent header="리스트 유형">
            <ButtonGroupField
              data-testid="productListType"
              name="type"
              width="100%"
              options={PRODUCT_LIST_TYPES.filter(
                (type) => type.canUseType.includes(value.props.badgeType),
              ) as Option<number, ReactNode>[]}
            />
          </FormContent>
          <FormContent header="뱃지 유형">
            <SelectField
              data-testid="productListbadgeType"
              name="badgeType"
              width="100%"
              // type이 3, carousel인 경우 options 선택 불가능하도록 제외
              options={PRODUCT_BADGE_TYPES.filter((badgeType) => badgeType.canUseType.includes(value.props.type))}
            />
          </FormContent>
        </>
      ) }

      <BuyButtonForm
        value={value}
        onChange={onChange}
      />

      { isManualSelection && (
        <ProductTextBadgeList
          type={value.props.type}
          badgeType={value.props.badgeType ?? 'none'}
          onChange={handleChangeTextBadgeList}
        />
      ) }

      { value.props.selectionType === 'RANKING' && (
        <FormContent header="랭킹 필터 기준">
          <SelectField
            data-testid="productListRankingFilterType"
            name="rankingFilterType"
            width="100%"
            options={RANKING_FILTER_TYPE_OPTIONS}
          />
        </FormContent>
      ) }
    </InspectorFormProvider>
  );
}

function BuyButtonForm({ value }: DocumentInspectorProps<ProductListProps>): ReactElement | null {
  const { type, badgeType, useBuyButton, selectionType } = value.props;
  const { children, ...others } = value ?? {};

  const { setLogData } = useStoreItemsContext();
  const { setValue } = useFormContext();

  const showUseBuyButton = ([1, 2] as ColType[]).includes(type) && badgeType !== 'banner';
  useEffect(() => {
    // useBuyButton true + 구매하기 버튼(showUseBuyButton) 노출 불가인 경우 -> false로 초기화
    if (useBuyButton && !showUseBuyButton) {
      setValue('useBuyButton', false);
    }
  }, [showUseBuyButton, useBuyButton]);

  return (
    <>
      {
        showUseBuyButton && selectionType === 'MANUAL'
          ? (
            <FormContent header="구매하기 버튼 사용">
              <ToggleField name="useBuyButton" />
            </FormContent>
          )
          : null
      }
      <LogDataFormGroup
        nodeKey={value?.key}
        {...setLogData?.(others)}
      />
    </>
  );
}
